import { toast } from "react-toastify";
import {
    VideoDto,
    VideoUpdateDto,
    VideoUploadRequestChunkedDto,
    VideoUploadRequestDto
} from "../Types/VideoTypes";

import {
    apiPublicGet,
    apiRawPost,
    apiRawPut,
    apiRawDelete
} from "./apiClient";

/* ====================================== */
/* PUBLIC: GET VIDEOS */
/* ====================================== */

const GetAllVideos = async (): Promise<VideoDto[] | undefined> => {
    try {
        return await apiPublicGet("/api/videos");
    } catch (err) {
        toast.error("Kunde inte hämta videos");
        return undefined;
    }
};

const GetVideo = async (id: number): Promise<VideoDto | undefined> => {
    try {
        return await apiPublicGet(`/api/videos/${id}`);
    } catch (err) {
        toast.error("Kunde inte hämta video");
        return undefined;
    }
};

/* ====================================== */
/* ADMIN: CREATE (normal upload) */
/* ====================================== */

const CreateVideo = async (data: {
    title: string;
    description: string;
    file: File;
}): Promise<VideoUploadRequestDto | undefined> => {
    try {
        const formData = new FormData();
        formData.append("Title", data.title);
        formData.append("Description", data.description);
        formData.append("File", data.file);

        const res = await apiRawPost("/api/videos", formData);

        if (!res.ok) {
            const msg = await res.text();
            toast.error(msg);
            return undefined;
        }

        toast.success("Video uppladdad!");
        return await res.json();
    } catch (err) {
        toast.error("Uppladdning misslyckades");
        return undefined;
    }
};

/* ====================================== */
/* ADMIN: Upload Any File (chunked-aware) */
/* ====================================== */

const UploadAnyFile = async ({
    title,
    description,
    uploadId,
    file
}: any) => {
    try {
        console.info("[VideoService] upload request preparing", {
            uploadId,
            title,
            fileName: file?.name,
            sizeBytes: file?.size,
            type: file?.type
        });

        const formData = new FormData();
        formData.append("uploadId", uploadId);
        formData.append("title", title ?? "");
        formData.append("description", description ?? "");
        formData.append("file", file);

        const res = await apiRawPost("/api/videos/upload", formData);
        if (!res.ok) {
            const msg = await res.text();
            console.warn("[VideoService] upload request failed", {
                uploadId,
                status: res.status,
                message: msg
            });
            toast.error(msg || "Uppladdning misslyckades");
            return undefined;
        }

        const json = await res.json();
        console.info("[VideoService] upload request completed", {
            uploadId,
            type: json?.type
        });
        return json;
    } catch (err) {
        console.error("[VideoService] upload request exception", { uploadId, err });
      toast.error("Uppladdning misslyckades");
      return undefined;
    }
};

/* ====================================== */
/* ADMIN: CHUNKED VIDEO UPLOAD */
/* ====================================== */

const UploadVideoChunked = async (data: {
    title: string;
    description: string;
    file: File;
    uploadId: string;
}): Promise<VideoUploadRequestChunkedDto | undefined> => {
    try {
        const formData = new FormData();
        formData.append("Title", data.title);
        formData.append("Description", data.description);
        formData.append("File", data.file);
        formData.append("UploadId", data.uploadId);

        const res = await apiRawPost("/api/videos/chunked", formData);

        if (!res.ok) {
            const msg = await res.text();
            toast.error(msg);
            return undefined;
        }

        toast.success("Video uppladdad (chunked)!");
        return await res.json();
    } catch (err) {
        toast.error("Chunked upload misslyckades");
        return undefined;
    }
};

/* ====================================== */
/* ADMIN: UPDATE */
/* ====================================== */

const UpdateVideo = async (
    id: number,
    dto: { title?: string; description?: string }
): Promise<VideoUpdateDto | undefined> => {
    try {
        const res = await apiRawPut(`/api/videos/${id}`, dto);

        if (!res.ok) {
            toast.warning("Uppdatering misslyckades");
            return undefined;
        }

        toast.success("Video uppdaterad");
        return await res.json();
    } catch (err) {
        toast.error("Fel vid uppdatering");
        return undefined;
    }
};

/* ====================================== */
/* ADMIN: DELETE */
/* ====================================== */

const DeleteVideo = async (id: number): Promise<boolean> => {
    try {
        const res = await apiRawDelete(`/api/videos/${id}`);

        if (res.status === 204) {
            toast.success("Video borttagen");
            return true;
        }

        toast.warning("Kunde inte ta bort video");
        return false;
    } catch (err) {
        toast.error("Fel vid borttagning");
        return false;
    }
};

/* ====================================== */
/* EXPORT */
/* ====================================== */

export const VideoService = {
    GetAllVideos,
    GetVideo,
    CreateVideo,
    UploadVideoChunked,
    UpdateVideo,
    DeleteVideo,
    UploadAnyFile
};
